import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useLearnAuth } from '@/hooks/useLearnAuth';
import { calculateOverallProgress } from '@/utils/progressTracking';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { toast } from 'sonner';
import { ArrowLeft, Shield, CheckCircle, XCircle } from 'lucide-react';

const checklistItems = [
  { id: 'consent', label: 'We obtain proper consent before collecting personal information' },
  { id: 'lawful', label: 'Data collection serves a lawful business purpose' },
  { id: 'minimization', label: 'We only collect data we genuinely need (data minimization)' },
  { id: 'secure', label: 'Personal data is stored securely with appropriate encryption' },
  { id: 'access', label: 'Access to personal data is restricted to authorized personnel only' },
  { id: 'retention', label: 'We have a clear data retention and deletion policy' },
  { id: 'rights', label: 'Users can access, correct, or delete their personal information' },
  { id: 'third_party', label: 'Third-party AI tools are vetted for privacy compliance' },
  { id: 'nz_residency', label: 'Data is stored in NZ or complies with Privacy Act 2020' },
  { id: 'breach', label: 'We have a data breach response plan' },
  { id: 'transparency', label: 'Our privacy practices are transparent and documented' },
  { id: 'training', label: 'Staff are trained on data privacy and AI ethics' },
  { id: 'audit', label: 'We conduct regular privacy audits and reviews' },
  { id: 'anonymization', label: 'Data is anonymized or pseudonymized where possible' },
  { id: 'accountability', label: 'Clear accountability for data protection decisions' }
];

const CapstonePrivacy: React.FC = () => {
  const { user } = useLearnAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingCapstone, setExistingCapstone] = useState<any>(null);
  
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!user) return;

    const checkEligibility = async () => {
      try {
        // Check if all 16 modules completed
        const progress = await calculateOverallProgress(user.id);
        if (progress.completedModules < 16) {
          toast.error('Complete all 16 modules before starting capstones');
          navigate('/learn/app/modules');
          return;
        }

        // Check for existing capstone
        const { data: capstoneData } = await supabase
          .from('capstones')
          .select('*')
          .eq('user_id', user.id)
          .eq('type', 'privacy')
          .maybeSingle();

        if (capstoneData) {
          setExistingCapstone(capstoneData);
          const privacyData = capstoneData.privacy_checklist_data as any;
          if (privacyData) {
            setChecklist(privacyData.checklist || {});
            setNotes(privacyData.notes || '');
          }
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [user, navigate]);

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setChecklist(prev => ({ ...prev, [id]: checked }));
  };

  const calculatePass = () => {
    const total = checklistItems.length;
    const checked = Object.values(checklist).filter(Boolean).length;
    return checked === total;
  };

  const getCompletionPercentage = () => {
    const total = checklistItems.length;
    const checked = Object.values(checklist).filter(Boolean).length;
    return Math.round((checked / total) * 100);
  };

  const handleSubmit = async (status: 'draft' | 'submitted') => {
    if (!user) return;

    // Validation for submission
    if (status === 'submitted' && !calculatePass()) {
      toast.error('All checklist items must be completed to submit');
      return;
    }

    setSubmitting(true);

    try {
      const capstoneData = {
        user_id: user.id,
        type: 'privacy',
        status,
        privacy_checklist_data: {
          checklist,
          notes,
          completedAt: new Date().toISOString()
        },
        privacy_checklist_pass: calculatePass()
      };

      if (existingCapstone) {
        const { error } = await supabase
          .from('capstones')
          .update(capstoneData)
          .eq('id', existingCapstone.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('capstones')
          .insert(capstoneData);

        if (error) throw error;
      }

      toast.success(status === 'submitted' ? 'Privacy Checklist submitted successfully!' : 'Progress saved');
      if (status === 'submitted') {
        navigate('/learn/app/dashboard');
      }
    } catch (error) {
      console.error('Error saving capstone:', error);
      toast.error('Failed to save capstone');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const isSubmitted = existingCapstone?.status === 'submitted';
  const passesChecklist = calculatePass();
  const completionPercentage = getCompletionPercentage();

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
          <Button variant="ghost" onClick={() => navigate('/learn/app/dashboard')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-brand-navy" />
              <div>
                <h1 className="text-3xl font-bold text-brand-navy">Privacy Checklist Capstone</h1>
                <p className="text-gray-600">Ensure your AI implementation complies with NZ privacy regulations</p>
              </div>
            </div>

            {isSubmitted && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">
                  ✅ Capstone submitted! 
                  {passesChecklist ? ' You passed all requirements.' : ' Review feedback from your assessor.'}
                </p>
              </div>
            )}

            {/* Progress Indicator */}
            <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-brand-navy">Completion Progress</span>
                <span className="text-2xl font-bold text-brand-navy">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-brand-navy rounded-full h-3 transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {passesChecklist ? (
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    All items complete - ready to submit!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Complete all items to pass (100% required)
                  </span>
                )}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4 text-brand-navy">Privacy Compliance Checklist</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Check each item that applies to your AI implementation. All items must be checked to pass.
                </p>

                <div className="space-y-4">
                  {checklistItems.map((item) => (
                    <div key={item.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                      <Checkbox
                        id={item.id}
                        checked={checklist[item.id] || false}
                        onCheckedChange={(checked) => handleCheckboxChange(item.id, checked as boolean)}
                        disabled={isSubmitted}
                      />
                      <Label
                        htmlFor={item.id}
                        className="text-sm font-normal cursor-pointer leading-relaxed"
                      >
                        {item.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Implementation Notes & Evidence</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe how you've implemented these privacy measures in your AI solution. Provide specific examples and evidence..."
                  rows={6}
                  disabled={isSubmitted}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Explain your privacy implementation approach, policies, and procedures
                </p>
              </div>

              {/* Actions */}
              {!isSubmitted && (
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    onClick={() => handleSubmit('draft')}
                    disabled={submitting}
                    className="flex-1"
                  >
                    Save Draft
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting || !passesChecklist}
                    className="flex-1"
                  >
                    Submit for Review
                  </Button>
                </div>
              )}

              {!passesChecklist && !isSubmitted && (
                <p className="text-sm text-center text-gray-500">
                  Complete all checklist items to enable submission
                </p>
              )}
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CapstonePrivacy;